import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/ChargeCalculationDetailPage.css";
import {
  chargeCalculationsService,
  ChargeCalculation,
  ChargeCalculationLine,
  AvailableInvoice,
} from "../services/charge-calculations.service";
import { formatCurrency, formatDate } from "../utils/format";
import { getErrorMessage, isHttpError } from '../utils/error';

const ChargeCalculationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [calculation, setCalculation] = useState<ChargeCalculation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Edit line modal
  const [editingLine, setEditingLine] = useState<ChargeCalculationLine | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editLabel, setEditLabel] = useState("");

  // Sort & filter
  type SortColumn = "providerName" | "label" | "expenseCategoryName" | "periodStartDate" | "daysInYear" | "prorataRatio" | "invoiceAmount" | "amount" | "isManual";
  type SortDirection = "asc" | "desc";
  const [sortColumn, setSortColumn] = useState<SortColumn>("providerName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterProvider, setFilterProvider] = useState<string>("");

  // Add invoice modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState<AvailableInvoice[]>([]);
  const [addFilterProvider, setAddFilterProvider] = useState<string>("");
  const [addError, setAddError] = useState<string | null>(null);
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await chargeCalculationsService.getById(id);
        setCalculation(data);
      } catch (err) {
        if (isHttpError(err, 404)) {
          setError("Calcul de charges introuvable.");
        } else {
          setError("Échec du chargement du calcul de charges.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleRecalculate = async () => {
    if (!calculation) return;
    if (
      !window.confirm(
        "Recalculer depuis les factures validées ?\n\nAttention : les lignes manuelles et les ajustements de montants seront perdus."
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const updated = await chargeCalculationsService.recalculate(calculation.id);
      setCalculation(updated);
    } catch (err) {
      setError("Erreur lors du recalcul.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!calculation) return;
    if (
      !window.confirm(
        `Supprimer le calcul de charges ${calculation.year} ? Cette action est irréversible.`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      await chargeCalculationsService.deleteCalculation(calculation.id);
      navigate("/charges");
    } catch (err) {
      setError("Erreur lors de la suppression.");
      setActionLoading(false);
    }
  };

  const isFinalized = calculation?.status === "finalized";

  const handleFinalize = async () => {
    if (!calculation) return;
    if (!window.confirm("Finaliser ce calcul de charges ?\n\nUne fois finalisé, aucune modification ne sera possible tant que vous ne le repasserez pas en brouillon.")) return;

    try {
      setActionLoading(true);
      const updated = await chargeCalculationsService.finalize(calculation.id);
      setCalculation(updated);
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de la finalisation."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfinalize = async () => {
    if (!calculation) return;
    if (!window.confirm("Repasser ce calcul en brouillon ?\n\nLes modifications seront à nouveau possibles.")) return;

    try {
      setActionLoading(true);
      const updated = await chargeCalculationsService.unfinalize(calculation.id);
      setCalculation(updated);
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors du passage en brouillon."));
    } finally {
      setActionLoading(false);
    }
  };

  // --- Edit line ---

  const openEditLine = (line: ChargeCalculationLine) => {
    setEditingLine(line);
    setEditAmount(String(line.amount));
    setEditLabel(line.label || "");
  };

  const handleSaveLine = async () => {
    if (!editingLine || !calculation) return;

    const newAmount = parseFloat(editAmount);
    if (isNaN(newAmount)) return;

    try {
      setActionLoading(true);
      const updatedLine = await chargeCalculationsService.updateLine(
        calculation.id,
        editingLine.id,
        {
          amount: newAmount,
          label: editLabel || undefined,
        }
      );
      // Update local state
      setCalculation((prev) => {
        if (!prev) return prev;
        const newLines = prev.lines.map((l) => (l.id === updatedLine.id ? updatedLine : l));
        const newTotal = newLines.reduce((sum, l) => sum + l.amount, 0);
        return { ...prev, lines: newLines, totalAmount: newTotal };
      });
      setEditingLine(null);
    } catch (err) {
      setError("Erreur lors de la mise à jour de la ligne.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Remove line ---

  const handleRemoveLine = async (line: ChargeCalculationLine) => {
    if (!calculation) return;
    const desc = line.label || "cette ligne";
    if (!window.confirm(`Supprimer "${desc}" ?`)) return;

    try {
      setActionLoading(true);
      await chargeCalculationsService.removeLine(calculation.id, line.id);
      setCalculation((prev) => {
        if (!prev) return prev;
        const newLines = prev.lines.filter((l) => l.id !== line.id);
        const newTotal = newLines.reduce((sum, l) => sum + l.amount, 0);
        return { ...prev, lines: newLines, totalAmount: newTotal };
      });
    } catch (err) {
      setError("Erreur lors de la suppression de la ligne.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Add invoice modal ---

  const handleOpenAddModal = async () => {
    if (!calculation) return;
    setShowAddModal(true);
    setAddFilterProvider("");
    setAddError(null);
    setLoadingAvailable(true);
    try {
      const invoices = await chargeCalculationsService.getAvailableInvoices(calculation.id);
      setAvailableInvoices(invoices);
    } catch {
      setAddError("Erreur lors du chargement des factures disponibles.");
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleAddInvoice = async (invoiceId: string) => {
    if (!calculation) return;
    try {
      setActionLoading(true);
      setAddError(null);
      const newLine = await chargeCalculationsService.addInvoiceToCalculation(calculation.id, invoiceId);
      setCalculation((prev) => {
        if (!prev) return prev;
        const newLines = [...prev.lines, newLine];
        const newTotal = newLines.reduce((sum, l) => sum + l.amount, 0);
        return { ...prev, lines: newLines, totalAmount: newTotal };
      });
      // Remove from available list
      setAvailableInvoices((prev) => prev.filter((i) => i.invoiceId !== invoiceId));
    } catch (err) {
      setAddError(getErrorMessage(err, "Erreur lors de l'ajout de la facture."));
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAvailableInvoices = useMemo(() => {
    if (!addFilterProvider) return availableInvoices;
    return availableInvoices.filter((i) => i.providerName === addFilterProvider);
  }, [availableInvoices, addFilterProvider]);

  const availableProviderNames = useMemo(() => {
    const names = new Set(availableInvoices.map((i) => i.providerName).filter(Boolean) as string[]);
    return Array.from(names).sort();
  }, [availableInvoices]);

  // --- Helpers ---

  const formatPeriodShort = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
    } catch {
      return "??";
    }
  };

  const formatPeriod = (line: ChargeCalculationLine): string => {
    if (!line.periodStartDate || !line.periodEndDate) return "-";
    return `${formatPeriodShort(line.periodStartDate)} → ${formatPeriodShort(line.periodEndDate)}`;
  };

  const formatProrata = (line: ChargeCalculationLine): string => {
    if (line.daysInYear == null || line.totalPeriodDays == null) return "-";
    return `${line.daysInYear}/${line.totalPeriodDays}`;
  };

  const formatRatio = (line: ChargeCalculationLine): string => {
    if (line.prorataRatio == null) return "-";
    return `${Math.round(line.prorataRatio * 100)}%`;
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const providerNames = useMemo(() => {
    if (!calculation) return [];
    const names = new Set(
      calculation.lines
        .map((l) => l.providerName)
        .filter((n): n is string => !!n)
    );
    return Array.from(names).sort((a, b) => a.localeCompare(b, "fr"));
  }, [calculation]);

  const sortedLines = useMemo(() => {
    if (!calculation) return [];
    let lines = [...calculation.lines];

    // Filter
    if (filterProvider) {
      lines = lines.filter((l) => l.providerName === filterProvider);
    }

    // Sort
    lines.sort((a, b) => {
      let cmp = 0;
      const col = sortColumn;

      const valA = a[col];
      const valB = b[col];

      if (valA == null && valB == null) cmp = 0;
      else if (valA == null) cmp = -1;
      else if (valB == null) cmp = 1;
      else if (typeof valA === "string" && typeof valB === "string") {
        cmp = valA.localeCompare(valB, "fr");
      } else if (typeof valA === "number" && typeof valB === "number") {
        cmp = valA - valB;
      } else if (typeof valA === "boolean" && typeof valB === "boolean") {
        cmp = Number(valA) - Number(valB);
      }

      return sortDirection === "asc" ? cmp : -cmp;
    });

    return lines;
  }, [calculation, sortColumn, sortDirection, filterProvider]);

  // Detect providers with incomplete year coverage
  const providerCoverageWarnings = useMemo(() => {
    if (!calculation || calculation.calculationMode === "invoice_date") return new Map<string, string>();
    const warnings = new Map<string, string>();
    const year = calculation.year;

    // Use UTC dates to avoid timezone issues
    const toUTCDay = (dateStr: string): number => {
      const d = new Date(dateStr);
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    };
    const formatUTCDay = (ts: number): string => {
      const d = new Date(ts);
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      return `${dd}/${mm}`;
    };
    const DAY = 86400000;
    const yearStart = Date.UTC(year, 0, 1);
    const yearEnd = Date.UTC(year, 11, 31);

    // Group lines by provider
    const byProvider = new Map<string, ChargeCalculationLine[]>();
    for (const line of calculation.lines) {
      const name = line.providerName || "-";
      if (!byProvider.has(name)) byProvider.set(name, []);
      byProvider.get(name)!.push(line);
    }

    Array.from(byProvider.entries()).forEach(([provider, providerLines]) => {
      const periods = providerLines
        .filter((l: ChargeCalculationLine) => l.periodStartDate && l.periodEndDate)
        .map((l: ChargeCalculationLine) => ({
          start: toUTCDay(l.periodStartDate!),
          end: toUTCDay(l.periodEndDate!),
        }))
        .sort((a: { start: number }, b: { start: number }) => a.start - b.start);

      if (periods.length === 0) return;

      const gaps: string[] = [];

      if (periods[0].start > yearStart) {
        gaps.push(`01/01 → ${formatUTCDay(periods[0].start - DAY)}`);
      }

      let covered = periods[0].end;
      for (let i = 1; i < periods.length; i++) {
        if (periods[i].start - covered > DAY) {
          gaps.push(`${formatUTCDay(covered + DAY)} → ${formatUTCDay(periods[i].start - DAY)}`);
        }
        if (periods[i].end > covered) covered = periods[i].end;
      }

      if (covered < yearEnd) {
        gaps.push(`${formatUTCDay(covered + DAY)} → 31/12`);
      }

      if (gaps.length > 0) {
        warnings.set(provider, `Périodes manquantes : ${gaps.join(", ")}`);
      }
    });

    return warnings;
  }, [calculation]);

  const statusLabel = (status: string) => {
    switch (status) {
      case "draft":
        return "Brouillon";
      case "finalized":
        return "Finalisé";
      default:
        return status;
    }
  };

  // --- Render ---

  if (loading) {
    return <div className="loading-indicator">Chargement du calcul de charges...</div>;
  }

  if (error && !calculation) {
    return <div className="error-message">{error}</div>;
  }

  if (!calculation) {
    return <div className="error-message">Calcul introuvable.</div>;
  }

  return (
    <div className="charge-detail-container">
      {/* Back link */}
      <button className="back-link" onClick={() => navigate("/charges")}>
        ← Retour à la liste
      </button>

      {/* Summary header */}
      <div className="charge-summary">
        <div className="charge-summary-main">
          <h2>Charges {calculation.year}</h2>
          <span className={`status-badge status-${calculation.status}`}>
            {statusLabel(calculation.status)}
          </span>
          <span className="mode-badge">
            {calculation.calculationMode === "invoice_date" ? "Date facture" : "Prorata"}
          </span>
        </div>
        <div className="charge-summary-total">
          <span className="total-label">Total</span>
          <span className="total-amount">{formatCurrency(calculation.totalAmount)}</span>
        </div>
        {calculation.notes && (
          <div className="charge-summary-notes">
            <span className="notes-label">Notes :</span> {calculation.notes}
          </div>
        )}
        <div className="charge-summary-meta">
          <span>Créé le {calculation.dateCreated ? formatDate(calculation.dateCreated) : "-"}</span>
          <span>Modifié le {calculation.dateModified ? formatDate(calculation.dateModified) : "-"}</span>
        </div>
        <div className="charge-summary-actions">
          {isFinalized ? (
            <button
              className="action-button"
              onClick={handleUnfinalize}
              disabled={actionLoading}
            >
              Repasser en brouillon
            </button>
          ) : (
            <>
              <button
                className="action-button finalize-button"
                onClick={handleFinalize}
                disabled={actionLoading}
              >
                Finaliser
              </button>
              <button
                className="action-button recalculate-button"
                onClick={handleRecalculate}
                disabled={actionLoading}
              >
                Recalculer
              </button>
              <button
                className="action-button add-line-button"
                onClick={handleOpenAddModal}
                disabled={actionLoading}
              >
                Ajouter une facture
              </button>
              <button
                className="action-button delete-button"
                onClick={handleDelete}
                disabled={actionLoading}
              >
                Supprimer le calcul
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Provider filter */}
      {providerNames.length > 1 && (
        <div className="provider-filter">
          <label htmlFor="provider-filter">Fournisseur :</label>
          <select
            id="provider-filter"
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
          >
            <option value="">Tous ({calculation.lines.length})</option>
            {providerNames.map((name) => (
              <option key={name} value={name}>
                {name}{providerCoverageWarnings.has(name) ? " ⚠" : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Coverage warnings */}
      {providerCoverageWarnings.size > 0 && !filterProvider && (
        <div className="coverage-warnings">
          {Array.from(providerCoverageWarnings.entries()).map(([provider, warning]) => (
            <div key={provider} className="coverage-warning-item">
              <span className="warning-icon">⚠</span>
              <strong>{provider}</strong> — {warning}
            </div>
          ))}
        </div>
      )}
      {filterProvider && providerCoverageWarnings.has(filterProvider) && (
        <div className="coverage-warnings">
          <div className="coverage-warning-item">
            <span className="warning-icon">⚠</span>
            <strong>{filterProvider}</strong> — {providerCoverageWarnings.get(filterProvider)}
          </div>
        </div>
      )}

      {/* Lines table */}
      <div className="lines-table-wrapper">
        <table className="lines-table">
          <thead>
            <tr>
              <th className="col-provider sortable-header" onClick={() => handleSort("providerName")}>Fournisseur{sortIndicator("providerName")}</th>
              <th className="col-label sortable-header" onClick={() => handleSort("label")}>Libellé{sortIndicator("label")}</th>
              <th className="col-category sortable-header" onClick={() => handleSort("expenseCategoryName")}>Catégorie{sortIndicator("expenseCategoryName")}</th>
              <th className="col-period sortable-header" onClick={() => handleSort("periodStartDate")}>Période{sortIndicator("periodStartDate")}</th>
              <th className="col-days sortable-header" onClick={() => handleSort("daysInYear")}>Jours{sortIndicator("daysInYear")}</th>
              <th className="col-ratio sortable-header" onClick={() => handleSort("prorataRatio")}>Prorata{sortIndicator("prorataRatio")}</th>
              <th className="col-invoice-amount sortable-header" onClick={() => handleSort("invoiceAmount")}>Montant facture{sortIndicator("invoiceAmount")}</th>
              <th className="col-amount sortable-header" onClick={() => handleSort("amount")}>Montant retenu{sortIndicator("amount")}</th>
              <th className="col-type sortable-header" onClick={() => handleSort("isManual")}>Type{sortIndicator("isManual")}</th>
              <th className="col-line-actions"></th>
            </tr>
          </thead>
          <tbody>
            {sortedLines.length > 0 ? (
              sortedLines.map((line) => (
                <tr
                  key={line.id}
                  className={`${line.isManual ? "line-manual" : ""} ${
                    line.isAdjusted ? "line-adjusted" : ""
                  }`}
                >
                  <td className="col-provider" title={providerCoverageWarnings.get(line.providerName || "-") || line.providerName || "-"}>
                    {line.providerName || "-"}
                    {providerCoverageWarnings.has(line.providerName || "-") && (
                      <span className="warning-icon-inline">⚠</span>
                    )}
                  </td>
                  <td className="col-label" title={line.label || "-"}>
                    {line.label || "-"}
                  </td>
                  <td className="col-category" title={line.expenseCategoryName || "-"}>
                    {line.expenseCategoryName || "-"}
                  </td>
                  <td className="col-period" title={formatPeriod(line)}>
                    {formatPeriod(line)}
                  </td>
                  <td className="col-days">{formatProrata(line)}</td>
                  <td className="col-ratio">{formatRatio(line)}</td>
                  <td className="col-invoice-amount">
                    {line.invoiceAmount != null ? formatCurrency(line.invoiceAmount) : "-"}
                  </td>
                  <td className="col-amount">
                    <span className={line.isAdjusted ? "adjusted-amount" : ""}>
                      {formatCurrency(line.amount)}
                    </span>
                    {line.isAdjusted && line.originalAmount != null && (
                      <span className="original-amount" title={`Montant original : ${formatCurrency(line.originalAmount)}`}>
                        ({formatCurrency(line.originalAmount)})
                      </span>
                    )}
                  </td>
                  <td className="col-type">
                    {line.isManual ? (
                      <span className="type-badge type-manual">Manuel</span>
                    ) : (
                      <span className="type-badge type-auto">Auto</span>
                    )}
                  </td>
                  <td className="col-line-actions">
                    {!isFinalized && (
                    <div className="line-actions-group">
                      <button
                        className="action-button edit-button"
                        onClick={() => openEditLine(line)}
                        title="Modifier"
                      >
                        Modifier
                      </button>
                      <button
                        className="action-button delete-button"
                        onClick={() => handleRemoveLine(line)}
                        title="Supprimer"
                      >
                        Suppr.
                      </button>
                    </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="no-lines-message">
                  Aucune ligne dans ce calcul. Cliquez sur "Recalculer" pour générer les lignes depuis
                  les factures validées, ou ajoutez des lignes manuelles.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit line modal */}
      {editingLine && (
        <div className="modal-overlay" onClick={() => setEditingLine(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Modifier la ligne</h3>
            <div className="edit-line-info">
              {editingLine.providerName && <p><strong>Fournisseur :</strong> {editingLine.providerName}</p>}
              {editingLine.label && <p><strong>Libellé :</strong> {editingLine.label}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="edit-label">Libellé</label>
              <input
                id="edit-label"
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-amount">Montant (€)</label>
              <input
                id="edit-amount"
                type="number"
                step="0.01"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
              />
            </div>
            {editingLine.originalAmount != null && (
              <p className="edit-original-info">
                Montant original : {formatCurrency(editingLine.originalAmount)}
              </p>
            )}
            <div className="modal-actions">
              <button className="action-button cancel-button" onClick={() => setEditingLine(null)}>
                Annuler
              </button>
              <button
                className="action-button"
                onClick={handleSaveLine}
                disabled={actionLoading}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add invoice modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Ajouter une facture au calcul {calculation?.year}</h3>

            {addError && <div className="modal-error">{addError}</div>}

            {loadingAvailable ? (
              <div className="loading-text">Chargement des factures...</div>
            ) : availableInvoices.length === 0 ? (
              <div className="empty-text">Aucune facture disponible pour ce calcul.</div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="add-filter-provider">Fournisseur :</label>
                  <select
                    id="add-filter-provider"
                    value={addFilterProvider}
                    onChange={(e) => setAddFilterProvider(e.target.value)}
                  >
                    <option value="">— Sélectionner un fournisseur —</option>
                    {availableProviderNames.map((name) => (
                      <option key={name} value={name}>
                        {name} ({availableInvoices.filter((i) => i.providerName === name).length})
                      </option>
                    ))}
                  </select>
                </div>

                {!addFilterProvider ? (
                  <div className="empty-text">Sélectionnez un fournisseur pour voir les factures disponibles.</div>
                ) : filteredAvailableInvoices.length === 0 ? (
                  <div className="empty-text">Aucune facture disponible pour ce fournisseur.</div>
                ) : (
                  <div className="available-invoices-list">
                    {filteredAvailableInvoices.map((inv) => (
                      <div key={inv.invoiceId} className="available-invoice-row">
                        <div className="available-invoice-info">
                          <span className="available-invoice-date">
                            {inv.invoiceDate ? formatDate(inv.invoiceDate) : "—"}
                          </span>
                          <span className="available-invoice-amount">{formatCurrency(inv.invoiceAmount)}</span>
                          {inv.periodStartDate && inv.periodEndDate && (
                            <span className="available-invoice-period">
                              {formatDate(inv.periodStartDate)} → {formatDate(inv.periodEndDate)}
                            </span>
                          )}
                          <span className="available-invoice-imputable">
                            Imputable : {formatCurrency(inv.amountForYear)}
                            {inv.prorataRatioForYear < 0.999 && (
                              <> ({Math.round(inv.prorataRatioForYear * 100)}%)</>
                            )}
                          </span>
                        </div>
                        <button
                          className="action-button add-invoice-button"
                          onClick={() => handleAddInvoice(inv.invoiceId)}
                          disabled={actionLoading}
                        >
                          Ajouter
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="modal-actions">
              <button
                className="action-button cancel-button"
                onClick={() => {
                  setShowAddModal(false);
                  setAddError(null);
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargeCalculationDetailPage;
